import { useRouter } from 'next/router'
import { Fragment, useMemo } from 'react'

import { MediaAsContents } from '@syconium/little-miss-figgy'

import { useFeatureFlag } from '../../../../../../app/_providers/ExperimentationProvider.client'
import { FILTERS_QUERY_PARAM } from '../../../../../../brunswick/constants'
import { useCollectionViewProductImpressions } from '../../../../../../lib/hooks/useCollectionViewProductImpressions'
import { deriveCollectionItemFilterInput } from '../../../../../../lib/utils/collection-filters'
import { Collection } from '../../Collection'
import { useCollectionGridSectionCollections } from '../../hooks/useCollectionGridSectionItems'
import {
  UseEditorializedCollectionsProps,
  useEditorializedCollections,
} from '../../hooks/useEditorializedCollectionItems'

import { Section } from './styles'
import { NoResultsTile } from './Tiles/NoResultsTile'

import type { AnalyticsContext } from '../../../../../../types/figs'
import type {
  CollectionGridSection as CollectionGridSectionQueryResults,
  Product,
  ProductColor,
  ProductFilter,
} from '../../../../../../types/graphql'

export type CollectionGridSectionProps = CollectionGridSectionQueryResults & {
  collectionViewHandle: string
}

type CollectionEditorialInfo = Omit<
  NonNullable<CollectionGridSectionProps['editorializedCollections']>[number],
  '__typename' | 'id' | 'handle'
>

export const CollectionGridSection = ({
  collectionViewHandle,
  filtersEnabled,
  groupedProductTiles,
  filterConfig,
  itemColorScope,
  id: sectionId,
  visualFiltersSection,
  visualFiltersSectionV2,
  editorializedCollections: editorializedCollectionsMetadata,
}: CollectionGridSectionProps) => {
  const analyticsContext: AnalyticsContext = useMemo(
    () => ({
      category: 'collection 2.0',
      list: collectionViewHandle,
    }),
    [collectionViewHandle]
  )

  const {
    query: {
      [FILTERS_QUERY_PARAM]: collectionFiltersQueryParam,
      category: legacyCategoryQueryParam,
      color: legacyColorQueryParam,
      fit: legacyFitQueryParam,
    },
  } = useRouter()

  useCollectionViewProductImpressions({ collectionViewHandle, sectionId }) // Track product impressions for this collection

  const renderV2VisualFilters = visualFiltersSectionV2?.id
  const renderV1VisualFilters = !renderV2VisualFilters && visualFiltersSection?.id

  const editorializedCollectionsMetadataMap = useMemo(() => {
    const editorializedCollectionsMetadataMap: Record<string, CollectionEditorialInfo> = {}

    editorializedCollectionsMetadata.forEach(next => {
      editorializedCollectionsMetadataMap[next.handle] = next
    })

    return editorializedCollectionsMetadataMap
  }, [editorializedCollectionsMetadata])

  const collectionHandles = useMemo(() => {
    return (
      editorializedCollectionsMetadata.map(editorializedCollectionMetadata => {
        return editorializedCollectionMetadata.handle
      }) ?? []
    )
  }, [editorializedCollectionsMetadata])

  const selectedFilters = useMemo(() => {
    return deriveCollectionItemFilterInput({
      collectionFiltersQueryParam,
      legacyCategoryQueryParam,
      legacyColorQueryParam,
      legacyFitQueryParam,
    })
  }, [
    collectionFiltersQueryParam,
    legacyCategoryQueryParam,
    legacyColorQueryParam,
    legacyFitQueryParam,
  ])

  const {
    data,
    fetchMore: loadMoreItems,
    status,
  } = useCollectionGridSectionCollections({
    collectionHandles,
    collectionColorScope: itemColorScope === 'ALL' ? 'ALL' : 'COLLECTION',
    filterConfig,
    selectedFilters,
    groupedProductTiles,
  })

  const totalItemCount = data?.numItems ?? 0
  const loadingInitial = status === 'pending-initial' || status === 'idle'
  const loadingMore = loadingInitial || status === 'pending-more'

  const colors: Record<string, ProductColor> = useMemo(() => {
    return (
      data?.colors?.reduce(
        (accum, curr) => ({
          ...accum,
          [curr.name]: { hexCodes: curr.hexCodes, name: curr.name, swatchUrl: curr.swatchUrl },
        }),
        {}
      ) ?? {}
    )
  }, [data?.colors])

  // TODO: Migrate off of this fake `ProductFilter type, instead, just stop casting it and allow the real type from gen:types to be leveraged.
  const filters = data?.filters as ProductFilter[] | undefined

  const collections: UseEditorializedCollectionsProps['collections'] = useMemo(() => {
    return (
      data?.collections.map(collection => {
        return {
          collectionScope: {
            collectionHandle: collection.handle,
            collectionColorScope: itemColorScope === 'ALL' ? 'ALL' : 'COLLECTION',
            pricingDisplay:
              editorializedCollectionsMetadataMap[collection.handle]?.pricingDisplay ??
              'priceRange',
          },
          // TODO: Stop casting to this fake Product type, migrate to using true query type as is without casting
          products: collection.items.nodes as unknown as Product[],
          editorialCellPlacements:
            editorializedCollectionsMetadataMap[collection.handle]?.editorialCellPlacements ?? [],
          editorialRows:
            editorializedCollectionsMetadataMap[collection.handle]?.editorialRows ?? [],
          title: editorializedCollectionsMetadataMap[collection.handle]?.title ?? undefined,
          subtitle: editorializedCollectionsMetadataMap[collection.handle]?.subtitle ?? undefined,
          numItems: collection.numItems,
        }
      }) ?? []
    )
  }, [data?.collections, editorializedCollectionsMetadataMap, itemColorScope])

  const editorializedCollections = useEditorializedCollections({
    collections,
    ignoreEditorials: Object.keys(selectedFilters).length > 0 && filtersEnabled,
  })
  const isFirstTileTitle = useMemo(
    () => editorializedCollections?.[0]?.items?.desktop?.[0]?.__typename === 'TitleTile',
    [editorializedCollections]
  )

  const colorSwatchDecision = useFeatureFlag({
    key: 'plp-color-swatch-test',
    defaultVariant: 'colorswatch-control',
    ifAccessedPriorToDecisionInitialization: 'return-null-while-pending',
  })

  return (
    <Section data-testid='collection-grid-section'>
      <Collection.Wrapper
        totalItemsCount={totalItemCount}
        heapContextAttributes={analyticsContext}
        colors={colors}
        filters={filtersEnabled ? filters : undefined}
      >
        {filtersEnabled && renderV2VisualFilters ? (
          <Collection.VisualFilters {...visualFiltersSectionV2} />
        ) : null}

        {filtersEnabled && renderV1VisualFilters ? (
          <Collection.VisualFilters {...visualFiltersSection} />
        ) : null}

        <Collection.Controls isLoading={loadingInitial} />

        <Collection.Grid isLoading={loadingInitial} hasVerticalMarginDesktop={!isFirstTileTitle}>
          <MediaAsContents lessThan='md'>
            {editorializedCollections.map(editorializedCollection => {
              return (
                <Fragment
                  key={`${editorializedCollection.collectionScope.collectionHandle}:mobile`}
                >
                  {editorializedCollection.items.mobile.map((item, index) => {
                    return (
                      <Collection.GridItem
                        key={`${editorializedCollection.collectionScope.collectionHandle}:mobile:${item.id}`}
                        item={item}
                        index={index}
                        colorSwatchDecision={colorSwatchDecision}
                      />
                    )
                  })}
                </Fragment>
              )
            })}
          </MediaAsContents>

          <MediaAsContents greaterThanOrEqual='md'>
            {editorializedCollections.map(editorializedCollection => {
              return (
                <Fragment
                  key={`${editorializedCollection.collectionScope.collectionHandle}:desktop`}
                >
                  {editorializedCollection.items.desktop.map((item, index) => {
                    return (
                      <Collection.GridItem
                        key={`${editorializedCollection.collectionScope.collectionHandle}:desktop:${item.id}`}
                        item={item}
                        index={index}
                        // REMOVE AFTER EXPERIMENT ENDS
                        colorSwatchDecision={colorSwatchDecision}
                      />
                    )
                  })}
                </Fragment>
              )
            })}
          </MediaAsContents>

          {totalItemCount === 0 && !loadingMore ? <NoResultsTile /> : null}
        </Collection.Grid>

        <Collection.Footer loadMore={loadMoreItems} />
      </Collection.Wrapper>
    </Section>
  )
}
